<script setup lang="ts">
  import currencyLib from 'currency-codes';
  import type { CurrencyCodeRecord } from 'currency-codes';
  import { computed } from 'vue';

  import { formatCurrency } from '@stuut/common/src/utils';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  const props = withDefaults(
    defineProps<{
      value: number;
      currency?: string | null;
      compact?: boolean;
      noTooltip?: boolean;
      majorUnit?: boolean;
      monotype?: boolean;
      // Append the ISO currency code (e.g. "$10.00 USD") and use tabular
      // figures so amounts line up in tables. Opt-in so the many existing
      // inline usages are unaffected.
      showCurrencyCode?: boolean;
    }>(),
    {
      monotype: true,
    },
  );

  const showCompact = computed<boolean>(
    // NOTE: using ?? because if props.compact is explicitly 'false', then showCompact should be 'false'
    () => props.compact ?? props.value > 9999999,
  );

  const currencyRecord = computed<CurrencyCodeRecord>(
    () => currencyLib.code(props.currency ?? 'USD') as CurrencyCodeRecord,
  );

  const expandedFormattedAmount = computed(() =>
    formatCurrency({
      currency: currencyRecord.value.code,
      majorUnit: props.majorUnit,
      value: props.value,
    }),
  );

  const formattedAmount = computed<string>(() =>
    formatCurrency({
      compact: showCompact.value,
      currency: currencyRecord.value.code,
      majorUnit: props.majorUnit,
      value: props.value,
    }),
  );
</script>

<template>
  <Tooltip :disabled="!showCompact || noTooltip">
    <TooltipTrigger as-child>
      <span
        v-bind="$attrs"
        :class="{ 'tabular-nums': showCurrencyCode }"
        :title="noTooltip ? expandedFormattedAmount : ''"
      >
        {{ formattedAmount }}
        <span v-if="showCurrencyCode" class="text-muted-foreground">{{ currencyRecord.code }}</span>
      </span>
    </TooltipTrigger>

    <TooltipContent>
      {{ expandedFormattedAmount }}
    </TooltipContent>
  </Tooltip>
</template>

<style scoped></style>
